/// <amd-module name="Release/DeploymentsSection/Stores/DeploymentsComponentStore" />
import * as VSSStore from "VSS/Reflux/Store";
import * as Utils_String from "VSS/Core/Util/String";
import { IVssPageContext } from "VSS/Platform/Context";

import {DeploymentsComponentActions} from "Release/DeploymentsSection/Actions/DeploymentsComponentActions";
import { IDeploymentsData } from "Release/DeploymentsSection/DeploymentsData";
import * as RMContracts from "Release/Client/Contracts/Release";
import { SignalRStore } from "Release/DeploymentsSection/Stores/SignalR";
import { DeploymentsSignalRHub } from "Release/DeploymentsSection/SignalRHub";
import { ReleaseContractsStatusConverter } from "Release/Helpers/ReleaseContractsStatusConverter";

export interface IDSWStoreProps {
    name: string;
}

export interface IDeploymentsState {
    succeededDeploymentsCount: number;
    inProgressDeploymentsCount: number;
    failedDeploymentsCount: number;
    partiallySucceededCount: number;
    deployments: Deployments[];
    renderCount: number;
    defaultRenderCount: number;
    isShowMoreVisible: boolean;
    triggerIssues: TriggerIssue[];
    expandIssues: boolean;
    noDeployments: boolean;
    signalRHubByProject: { [key: string]: DeploymentsSignalRHub };
}

export class Deployments {
    environments: RMContracts.ReleaseEnvironment[];
    id: number;
    rdName: string;
    rdUrl: string;
    rdId: number;
    releaseName: string;
    releaseUrl: string;
    isFavorite: boolean;
    projectId: string
}

export class TriggerIssue {
    releaseDefinitionUrl: string;
    releaseDefinitionName: string;
    issueMessage: string;
    isSystemIssue: boolean;
    isError: boolean;
    isWarning: boolean;
}

export class DeploymentsComponentStore extends VSSStore.Store {
    constructor(actions: DeploymentsComponentActions, signalRStore: SignalRStore) {
        super("deploymentsStoreChanged");
        this._actions = actions;
        this._signalRStore = signalRStore;
        this._initializeState();
        this._actions.maximizeRenderIndex.addListener(this._maximizeRenderIndex);
        this._actions.incrementRenderIndex.addListener(this._incrementRenderIndex);
        this._actions.resetView.addListener(this._resetView);
        this._actions.setDeploymentsData.addListener(this._loadDeploymentsData);
        this._actions.toggleExpandIssues.addListener(this._toggleExpandIssues);
        this._signalRStore.addEnvironmentsUpdatedListener(this._environmentUpdated);
    }

    public dispose(): void {
        this._actions.maximizeRenderIndex.removeListener(this._maximizeRenderIndex);
        this._actions.incrementRenderIndex.removeListener(this._incrementRenderIndex);
        this._actions.resetView.removeListener(this._resetView);
        this._actions.setDeploymentsData.removeListener(this._loadDeploymentsData);
        this._actions.toggleExpandIssues.removeListener(this._toggleExpandIssues);
        this._signalRStore.removeEnvironmentsUpdatedListener(this._environmentUpdated);
    }

    public static getKey(): string {
        return "deployments-section-store";
    }

    public static getDefaultState(): IDeploymentsState {
        return {
            failedDeploymentsCount: 0,
            inProgressDeploymentsCount: 0,
            succeededDeploymentsCount: 0,
            partiallySucceededCount: 0,
            deployments: [],
            renderCount: 0,
            defaultRenderCount: 0,
            isShowMoreVisible: true,
            triggerIssues: [],
            expandIssues: false,
            noDeployments: true,
            signalRHubByProject: {}
        };   
    }

    public getState(): IDeploymentsState {
        return this._state;
    }

    private _setFavorites(favorites: string[]): void {
        if (!favorites) {
            return;
        }

        favorites.map((favoriteId: string) => {
            this._allFavoriteDefinitions[favoriteId] = true;
        });
    }

    private _initializeState(): void {
        this._allFavoriteDefinitions = {};
        this._state = DeploymentsComponentStore.getDefaultState();
    }

    private _setDeploymentsData(releases: RMContracts.Release[]): void {
        this._state.deployments = [];
        if (!releases) {
            return;
        }

        let favoriteReleases: RMContracts.Release[] = [];
        let allReleases: RMContracts.Release[] = [];
        let failedReleases: RMContracts.Release[] = [];
        this._state.succeededDeploymentsCount = 0;
        this._state.inProgressDeploymentsCount = 0;
        this._state.failedDeploymentsCount = 0;
        this._state.partiallySucceededCount = 0;

        this._state.noDeployments = releases.length > 0 ? false : true;

        releases.forEach(release => {
            this._countDeploymentsStatus(release);
            if (this._isFavoriteDefinition(release.projectReference.id, release.releaseDefinition.id.toString())) {
                favoriteReleases.push(release);
            } else if (this._consistsFailedDeployments(release.environments)) {
                failedReleases.push(release);
            } else {
                allReleases.push(release);
            }
        });

        let favoriteDeployments = this._getDeployments(favoriteReleases, true);
        let failedDeployments = this._getDeployments(failedReleases);
        let allDeployments = this._getDeployments(allReleases);
        this._state.renderCount = releases.length - allReleases.length;
        this._state.defaultRenderCount = this._state.renderCount;
        this._state.deployments = favoriteDeployments.concat(failedDeployments).concat(allDeployments);
        if (this._state.renderCount < this._state.deployments.length) {
            this._state.isShowMoreVisible = true;
        } else {
            this._state.isShowMoreVisible = false;
        }
    }

    private _isFavoriteDefinition(projectId: string, definitionId: string): boolean {
        let favoriteKey = projectId + "/" + definitionId;
        if (favoriteKey in this._allFavoriteDefinitions) {
            return true;
        }

        return false;
    }

    private _consistsFailedDeployments(environments: RMContracts.ReleaseEnvironment[]) {
        let failedDeployments = environments.filter(environment => environment.status === RMContracts.EnvironmentStatus.Rejected
                                                                ||  environment.status === RMContracts.EnvironmentStatus.Canceled);

        return failedDeployments.length > 0 ? true : false;
    }

    private _getDeployments(releases: RMContracts.Release[], isFavorite: boolean = false): Deployments[] {
        return releases.map(release => {
            return {
                environments: release.environments,
                id: release.id,
                rdName: release.releaseDefinition.name,
                rdUrl: release.releaseDefinition._links.web.href,
                rdId: release.releaseDefinition.id,
                releaseName: release.name.toString(),
                releaseUrl: release._links.web.href,
                isFavorite: isFavorite,
                projectId: release.projectReference.id
            };
        });
    }

    private _countDeploymentsStatus(release: RMContracts.Release) {
        release.environments.forEach(environment => {
            let deploySteps = environment.deploySteps;
            if (deploySteps.length > 0) {
                const latestAttempt = Math.max(...deploySteps.map(deployStep => deployStep.attempt));
                let latestDeployment = deploySteps.find(deployStep => deployStep.attempt === latestAttempt);
                this._incrementStatusCount(latestDeployment.status);
            }
        });
    }

    private _maximizeRenderIndex = (): void => {
        this._state.renderCount = this._state.deployments.length;
        this._state.isShowMoreVisible = false;
        this.emitChanged();
    }

    private _incrementRenderIndex = (): void => {
        this._state.renderCount += DeploymentsComponentStore._defaultIncrementRenderCount;
        if (this._state.renderCount >= this._state.deployments.length) {
            this._state.isShowMoreVisible = false;
        }

        this.emitChanged();
    }

    private _resetView = (): void => {
        this._state.renderCount = this._state.defaultRenderCount;
        if (this._state.renderCount <= this._state.deployments.length) {
            this._state.isShowMoreVisible = true;
        }

        this.emitChanged();
    }

    private _setTriggerIssues(issues: RMContracts.AutoTriggerIssue[]): void {
        this._state.triggerIssues = [];
        if (!!issues && issues.length > 0) {
            issues.sort((x, y) => Utils_String.localeIgnoreCaseComparer(x.releaseDefinitionReference.name, y.releaseDefinitionReference.name));
            issues.forEach((autoTriggerIssue: RMContracts.AutoTriggerIssue) => {
                this._state.triggerIssues.push(this._getReleaseTriggerIssue(autoTriggerIssue));
            });
        }
    }

    private _getReleaseTriggerIssue(releaseTriggerIssue: RMContracts.AutoTriggerIssue): TriggerIssue {
        let project = releaseTriggerIssue.project.name || releaseTriggerIssue.project.id;
        let releaseDefinitionName = releaseTriggerIssue.releaseDefinitionReference.name;
        let issueMessage = releaseTriggerIssue.issue.message;
        let releaseDefinitionUrl = releaseTriggerIssue.releaseDefinitionReference._links.web.href;
        let isSystemIssue = false;
        let isError = false;
        let isWarning = false;

        if (Utils_String.localeIgnoreCaseComparer(releaseTriggerIssue.issueSource.toString(), "System") === 0) {
            isSystemIssue = true;
        }

        if (Utils_String.localeIgnoreCaseComparer(releaseTriggerIssue.issue.issueType, DeploymentsComponentStore._errorIssueType) === 0) {
            isError = true;
        }
        else if (Utils_String.localeIgnoreCaseComparer(releaseTriggerIssue.issue.issueType, DeploymentsComponentStore._warningIssueType) === 0) {
            isWarning = true;
        }

        return {
            releaseDefinitionUrl: releaseDefinitionUrl,
            releaseDefinitionName: releaseDefinitionName,
            issueMessage: issueMessage,
            isSystemIssue: isSystemIssue,
            isError: isError,
            isWarning: isWarning
        };
    }

    private _loadDeploymentsData = (deploymentsData: IDeploymentsData) => {
        this._setFavorites(deploymentsData.favoriteDefinitionIds);
        this._setTriggerIssues(deploymentsData.autoTriggerIssues);
        this._setDeploymentsData(deploymentsData.releases);
        this.emitChanged();
    }

    private _toggleExpandIssues = () => {
        this._state.expandIssues = !this._state.expandIssues;
        this.emitChanged();
    }

    private _environmentUpdated = (environmentUpdatedEvent: RMContracts.ReleaseEnvironmentStatusUpdatedEvent) => {
        if (!environmentUpdatedEvent) {
            return;
        }
        
        environmentUpdatedEvent.environmentStatus = ReleaseContractsStatusConverter.toEnvironmentStatus(environmentUpdatedEvent.environmentStatus.toString());
        let releaseId = environmentUpdatedEvent.releaseId;
        let updatedDeploymentIndex = this._state.deployments.findIndex(i => i.id === releaseId);
        if (updatedDeploymentIndex >= 0) {
            let environmentToBeUpdatedIndex = this._state.deployments[updatedDeploymentIndex].environments.findIndex(i => i.id === environmentUpdatedEvent.environmentId);
            if (environmentToBeUpdatedIndex >= 0) {
                let latestDeploymentStatus = ReleaseContractsStatusConverter.toDeploymentStatus(environmentUpdatedEvent.latestDeploymentStatus.toString());
                let latestDeploymentOperationStatus =  ReleaseContractsStatusConverter.toDeploymentOperationStatus(environmentUpdatedEvent.latestDeploymentOperationStatus.toString());
                this._updateDeploymentsCount(this._state.deployments[updatedDeploymentIndex].environments[environmentToBeUpdatedIndex], latestDeploymentStatus);
                
                let deploySteps = this._state.deployments[updatedDeploymentIndex].environments[environmentToBeUpdatedIndex].deploySteps;
                const latestAttempt = Math.max(...deploySteps.map(deployStep => deployStep.attempt));
                let latestDeployment = deploySteps.find(deployStep => deployStep.attempt === latestAttempt);
                if (!latestDeployment) {
                    latestDeployment = this._createDummyDeployment();
                    deploySteps.push(latestDeployment);
                }

                latestDeployment.status = latestDeploymentStatus;
                latestDeployment.operationStatus =  latestDeploymentOperationStatus;
                this._state.deployments[updatedDeploymentIndex].environments[environmentToBeUpdatedIndex].status = environmentUpdatedEvent.environmentStatus;
                
                if (latestDeployment.status !== RMContracts.DeploymentStatus.NotDeployed) {
                    this._state.deployments[updatedDeploymentIndex].environments[environmentToBeUpdatedIndex].preDeployApprovals = [];
                }
                
                if (latestDeployment.status == RMContracts.DeploymentStatus.Succeeded || latestDeployment.status == RMContracts.DeploymentStatus.Failed
                            || latestDeployment.status == RMContracts.DeploymentStatus.PartiallySucceeded) {
                    this._state.deployments[updatedDeploymentIndex].environments[environmentToBeUpdatedIndex].postDeployApprovals = [];
                }

                this._state.deployments[updatedDeploymentIndex].environments[environmentToBeUpdatedIndex].status = environmentUpdatedEvent.environmentStatus;
                this._state.deployments = this._state.deployments.slice(0);
                this.emitChanged();
            }
        }
    }

    private _updateDeploymentsCount(environmentToBeUpdated: RMContracts.ReleaseEnvironment, updatedEnvironment: RMContracts.DeploymentStatus): void {
        let deploySteps = environmentToBeUpdated.deploySteps;
        if (deploySteps.length > 0) {
            const latestAttempt = Math.max(...deploySteps.map(deployStep => deployStep.attempt));
            let latestDeployment = deploySteps.find(deployStep => deployStep.attempt === latestAttempt);
            this._decrementStatusCount(latestDeployment.status);
        }

        this._incrementStatusCount(updatedEnvironment);
    }

    private _incrementStatusCount(deploymentStatus: RMContracts.DeploymentStatus) {
        switch (deploymentStatus) {
            case RMContracts.DeploymentStatus.Succeeded:
                this._state.succeededDeploymentsCount++;
                break;
            case RMContracts.DeploymentStatus.InProgress:
                this._state.inProgressDeploymentsCount++;
                break;
            case RMContracts.DeploymentStatus.Failed:
                this._state.failedDeploymentsCount++;
                break;
            case RMContracts.DeploymentStatus.PartiallySucceeded:
                this._state.partiallySucceededCount++;
        }
    }

    private _decrementStatusCount(deploymentStatus: RMContracts.DeploymentStatus) {
        switch (deploymentStatus) {
            case RMContracts.DeploymentStatus.Succeeded:
                this._state.succeededDeploymentsCount--;
                break;
            case RMContracts.DeploymentStatus.InProgress:
                this._state.inProgressDeploymentsCount--;
                break;
            case RMContracts.DeploymentStatus.Failed:
                this._state.failedDeploymentsCount--;
                break;
            case RMContracts.DeploymentStatus.PartiallySucceeded:
                this._state.partiallySucceededCount--;
        }
    }

    private _createDummyDeployment(): RMContracts.DeploymentAttempt {
        return {
            attempt: 1,
            deploymentId: 0,
            hasStarted: false,
            id: 0,
            errorLog: "",
            issues: [],
            job: null,
            lastModifiedBy: null,
            lastModifiedOn: null,
            operationStatus: null,
            postDeploymentGates: null,
            preDeploymentGates: null,
            queuedOn: null,
            reason: null,
            releaseDeployPhases: null,
            requestedBy: null,
            requestedFor: null,
            runPlanId: null,
            status: null,
            tasks: []
        }
    }

    private _state: IDeploymentsState;
    private _actions: DeploymentsComponentActions;
    private _allFavoriteDefinitions: { [key: string]: boolean } = {};
    private _signalRStore: SignalRStore;
    private static _defaultIncrementRenderCount: number = 10;
    private static _errorIssueType: string = "Error";
    private static _warningIssueType: string = "Warning";
}